<template>
  <div class="occ-tl">
    <div v-if="loading" class="text-center py-3">
      <b-spinner small variant="primary" />
    </div>

    <div v-else-if="!logs.length" class="occ-tl-empty">
      <feather-icon icon="ClockIcon" size="22" class="mb-1" />
      <p class="mb-0">Nenhum evento registrado.</p>
    </div>

    <div v-else class="occ-tl-list">
      <div
        v-for="(log, idx) in logs"
        :key="log.id || idx"
        class="occ-tl-item"
      >
        <!-- Trilho: dot + conector -->
        <div class="occ-tl-rail">
          <div
            class="occ-tl-dot"
            :class="{ 'occ-tl-dot--current': idx === logs.length - 1 }"
            :style="dotStyle(log.to_step, idx === logs.length - 1)"
          />
          <div
            v-if="idx < logs.length - 1"
            class="occ-tl-connector"
            :style="{ borderLeftColor: stepColor(log.to_step) + '35' }"
          />
        </div>

        <!-- Conteúdo -->
        <div class="occ-tl-body" :class="{ 'occ-tl-body--last': idx === logs.length - 1 }">
          <div class="occ-tl-row">
            <span class="occ-tl-step" :style="{ color: stepColor(log.to_step) }">
              {{ stepLabel(log.to_step) }}
            </span>
            <span class="occ-tl-date">{{ formatDate(log.created_at) }}</span>
          </div>

          <!-- Badge de duração -->
          <div class="occ-tl-duration-row mb-1">
            <span
              v-if="idx < logs.length - 1"
              class="occ-tl-duration occ-tl-duration--done"
            >
              <feather-icon icon="ClockIcon" size="10" class="mr-25" />
              {{ stepDuration(idx) }}
            </span>
            <span
              v-else
              class="occ-tl-duration occ-tl-duration--active"
              :style="{ color: stepColor(log.to_step), borderColor: stepColor(log.to_step) + '50', backgroundColor: stepColor(log.to_step) + '12' }"
            >
              <span class="occ-tl-pulse" :style="{ backgroundColor: stepColor(log.to_step) }" />
              {{ stepDuration(idx) }} · em andamento
            </span>
          </div>

          <div v-if="log.user" class="occ-tl-meta">
            <feather-icon icon="UserIcon" size="11" class="mr-25" />
            {{ log.user.name }}
          </div>
          <div v-if="log.from_step" class="occ-tl-transition">
            <feather-icon icon="CornerDownRightIcon" size="11" class="mr-25" />
            Encaminhado de <em>{{ stepLabel(log.from_step) }}</em>
          </div>
          <div v-if="log.observation" class="occ-tl-obs">
            "{{ log.observation }}"
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'

export default {
  name: 'OccurrenceTimeline',
  components: { BSpinner },
  props: {
    occurrenceId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      logs: [],
      loading: false,
      now: new Date(),
      ticker: null,
    }
  },
  mounted() {
    this.fetchLogs()
    this.ticker = setInterval(() => { this.now = new Date() }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.ticker)
  },
  methods: {
    async fetchLogs() {
      this.loading = true
      try {
        const res = await this.$http.get(
          this.$store.state.api + `occurrences/${this.occurrenceId}/step-logs`
        )
        this.logs = res.data.data || res.data || []
      } catch (e) {
        this.logs = []
      } finally {
        this.loading = false
      }
    },

    stepDuration(idx) {
      const from = new Date(this.logs[idx].created_at)
      const to = idx < this.logs.length - 1
        ? new Date(this.logs[idx + 1].created_at)
        : this.now

      const totalSeconds = Math.max(0, Math.floor((to - from) / 1000))
      const days    = Math.floor(totalSeconds / 86400)
      const hours   = Math.floor((totalSeconds % 86400) / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60

      const parts = []
      if (days > 0)    parts.push(`${days} dia${days > 1 ? 's' : ''}`)
      if (hours > 0)   parts.push(`${hours} hora${hours > 1 ? 's' : ''}`)
      if (minutes > 0) parts.push(`${minutes} minuto${minutes > 1 ? 's' : ''}`)
      if (seconds > 0 || parts.length === 0) parts.push(`${seconds} segundo${seconds !== 1 ? 's' : ''}`)

      if (parts.length === 1) return parts[0]
      return parts.slice(0, -1).join(', ') + ' e ' + parts[parts.length - 1]
    },

    stepLabel(step) {
      const labels = {
        solicita: 'Solicitação',
        triagem: 'Triagem',
        regulacao: 'Atendimento',
        despacha: 'Despacho',
        em_atendimento: 'Em Atendimento',
        encerrado: 'Encerrado',
      }
      if (labels[step]) return labels[step]
      if (!step) return '—'
      return step.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },

    stepColor(step) {
      const colors = {
        solicita: '#6B7280',
        triagem: '#F59E0B',
        regulacao: '#2563EB',
        despacha: '#7C3AED',
        em_atendimento: '#0891B2',
        encerrado: '#16A34A',
      }
      return colors[step] || '#6B7280'
    },

    dotStyle(step, isCurrent) {
      const color = this.stepColor(step)
      return isCurrent
        ? { backgroundColor: color, borderColor: color }
        : { backgroundColor: '#fff', borderColor: color }
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.occ-tl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: #b9b9c3;
  font-size: 13px;
  gap: 6px;
}

.occ-tl-list {
  display: flex;
  flex-direction: column;
}

.occ-tl-item {
  display: flex;
  gap: 14px;
}

/* ── Trilho ── */
.occ-tl-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 16px;
  flex-shrink: 0;
  padding-top: 2px;
}

.occ-tl-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2.5px solid;
  flex-shrink: 0;
  transition: background 0.2s;
}

.occ-tl-dot--current {
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
}

.occ-tl-connector {
  flex: 1;
  border-left: 2px dashed;
  min-height: 16px;
  margin-top: 5px;
  margin-bottom: 5px;
}

/* ── Corpo ── */
.occ-tl-body {
  flex: 1;
  padding-bottom: 22px;
  min-width: 0;
}

.occ-tl-body--last {
  padding-bottom: 4px;
}

.occ-tl-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.occ-tl-step {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
}

.occ-tl-date {
  font-size: 11px;
  color: #b9b9c3;
  white-space: nowrap;
  flex-shrink: 0;
  padding-top: 1px;
}

/* ── Duração ── */
.occ-tl-duration-row {
  display: flex;
  align-items: center;
}

.occ-tl-duration {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  border: 1px solid;
  white-space: nowrap;
}

.occ-tl-duration--done {
  color: #b9b9c3;
  border-color: #ebe9f1;
  background: #f8f7ff;
}

.occ-tl-duration--active {
  /* color/border/bg via inline style */
  font-weight: 700;
}

.occ-tl-pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 6px;
  animation: tl-pulse 1.6s ease-in-out infinite;
}

@keyframes tl-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(0.7); }
}

.occ-tl-meta {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #6e6b7b;
  margin-bottom: 3px;
}

.occ-tl-transition {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #b9b9c3;
  margin-bottom: 3px;
}

.occ-tl-obs {
  margin-top: 6px;
  padding: 7px 10px;
  background: #f8f7ff;
  border-left: 2px solid #7367f0;
  border-radius: 0 6px 6px 0;
  font-size: 12px;
  font-style: italic;
  color: #6e6b7b;
}

/* dark */
.dark-layout .occ-tl-dot {
  background-color: #283046 !important;
}

.dark-layout .occ-tl-duration--done {
  background: #1c2333;
  border-color: #3b4253;
  color: #676d7d;
}

.dark-layout .occ-tl-meta {
  color: #d0d2d6;
}

.dark-layout .occ-tl-obs {
  background: #1c2333;
  color: #d0d2d6;
}
</style>
